// Pure decisions for the two 0.11.7 nudges, kept out of the container so
// they can be unit-tested without a browser.

const DAY_MS = 24 * 60 * 60 * 1000;

// The dashboard's cap-triggered offer applies once this install has hit the
// weekly cap `afterCaps` times. The count comes from the background's usage
// analytics; it is the same number attached to checkout, so what the popup
// shows and what the unlock page charges always agree.
export function isPromoQualified({ usagePromo, weeklyCapCount } = {}) {
  const afterCaps = Number(usagePromo?.afterCaps);
  if (!usagePromo?.promoId || !Number.isInteger(afterCaps) || afterCaps < 1) {
    return false;
  }
  return (Number(weeklyCapCount) || 0) >= afterCaps;
}

function toTime(value) {
  const parsed = value ? new Date(value).getTime() : NaN;
  return Number.isFinite(parsed) ? parsed : 0;
}

// Ask for a rating only from people who use Slowed a lot: Pro, or enough
// real listening to have an opinion. Never while they are out of minutes,
// staring at YouTube Music's Pro lock, or still in the trial — a trial user
// is days away from "trial ended", and those are the moments that earn
// one-star reviews, not five.
//
// And never nag: a show is remembered, the next one waits `cooldownDays`,
// "Not now" waits `snoozeDays`, and after `maxShows` it is never asked
// again. Rating once ends it for good.
export function computeRatePromptVisible({
  isRateAppEnabled,
  isPro,
  trialMode,
  trialEnded,
  usageHit,
  musicProOnly,
  notSupported,
  reportedMinutes,
  afterMinutes = 180,
  maxShows = 3,
  cooldownDays = 7,
  ratePrompt,
  now = Date.now(),
} = {}) {
  if (isRateAppEnabled === false) return false;
  if (usageHit === true || musicProOnly === true || notSupported === true) {
    return false;
  }
  if (ratePrompt?.completedAt) return false;
  if ((Number(ratePrompt?.shownCount) || 0) >= (Number(maxShows) || 0)) {
    return false;
  }
  if (toTime(ratePrompt?.snoozedUntil) > now) return false;
  const lastShownAt = toTime(ratePrompt?.lastShownAt);
  if (lastShownAt && now - lastShownAt < (Number(cooldownDays) || 0) * DAY_MS) {
    return false;
  }

  if (isPro === true) return true;
  if (trialMode === true && trialEnded !== true) return false;
  return (Number(reportedMinutes) || 0) >= (Number(afterMinutes) || 0);
}

// Persisted the moment the banner is rendered, so a popup that is simply
// closed still counts as one of the few asks.
export function markRatePromptShown(ratePrompt, { now = Date.now() } = {}) {
  return {
    ...(ratePrompt || {}),
    shownCount: (Number(ratePrompt?.shownCount) || 0) + 1,
    lastShownAt: new Date(now).toISOString(),
  };
}

export function snoozeRatePrompt(
  ratePrompt,
  { days = 30, now = Date.now() } = {},
) {
  return {
    ...(ratePrompt || {}),
    snoozedUntil: new Date(now + days * DAY_MS).toISOString(),
  };
}

export function completeRatePrompt(ratePrompt, { now = Date.now() } = {}) {
  return {
    ...(ratePrompt || {}),
    completedAt: new Date(now).toISOString(),
  };
}
